/**
 * spaceLevelSession.ts
 * Per-level session helpers for World 2 (Starfield Drift).
 *
 * The shmup screen has no score field or checkpoint system today, so this
 * is intentionally smaller than its World 1 counterpart
 * ({@link ../levels/level1Session.persistWorld1Completion}). It does two
 * things:
 *  - flips `levels[levelId].completed = true` so the hub world-map can
 *    render the "X / 4 stages cleared" count for W2, and
 *    {@link ../data/worldUnlock.isWorldUnlocked} flips W3 to unlocked
 *    once `levels['2-B'].completed === true`,
 *  - merges any Constellation Shard ids collected during the session
 *    (via {@link ../engine/spaceShardSystem.getCollectedShardIdsThisSession})
 *    into `levels[levelId].collectedConstellationShardIds`, which the
 *    pre-existing
 *    {@link ../data/worldUnlock.isSecretStageUnlocked} gate counts to
 *    unlock 2-S,
 *  - merges any Star Seed loot-drop ids collected during the session
 *    (via {@link ../engine/spaceLootSystem.getCollectedSeedIdsThisSession})
 *    into `levels[levelId].collectedStarSeedIds`.
 *
 * DECISION: Snapshot the collected-id list BEFORE the first `await` and
 * merge with the prior save state (rather than clobbering) so an
 * out-of-band write to the same key from a parallel slice (e.g. a
 * future seed primitive) can't be stomped on by this writer.
 *
 * @module spaceLevelSession
 */

import SaveManager from '../services/saveManager';
import Logger from '../utils/logger';
import { mergeIds } from './mergeIds';

/**
 * Persist the just-completed World 2 level to the save file. Fire-and-forget:
 * failures are logged via {@link Logger.error}, never thrown, so the
 * Sector-Cleared overlay never has to wait on disk I/O.
 *
 * @param levelId            - Save key for the completed level (e.g. `'2-1'`, `'2-B'`).
 * @param collectedShardIds  - Shard ids picked up during the session. Defaults
 *   to `[]` so legacy callers (boss arena, tests that don't exercise the
 *   shard primitive) keep working unchanged.
 * @param collectedSeedIds   - Star Seed loot-drop ids picked up during the
 *   session. Defaults to `[]` for the same reason.
 * @returns A promise that resolves when the write completes (or fails).
 */
export async function persistSpaceLevelCompletion(
  levelId: string,
  collectedShardIds: readonly string[] = [],
  collectedSeedIds: readonly string[] = [],
): Promise<void> {
  // DECISION: Snapshot the input arrays before the first await so a fast
  // restart can't mutate the upstream session lists between load() and
  // the read.
  const shardSnapshot = collectedShardIds.slice();
  const seedSnapshot = collectedSeedIds.slice();
  try {
    const existing = (await SaveManager.load()) ?? SaveManager.createDefaultSave();
    const prev = existing.levels[levelId];
    existing.levels[levelId] = {
      completed: true,
      collectedStarSeedIds: mergeIds(
        prev?.collectedStarSeedIds,
        seedSnapshot,
      ),
      collectedConstellationShardIds: mergeIds(
        prev?.collectedConstellationShardIds,
        shardSnapshot,
      ),
      collectedHeartFragmentIds: prev?.collectedHeartFragmentIds ?? [],
      // DECISION: Space levels do not run the checkpoint system, so the
      // existing checkpoint id (if any) is meaningless after a sector
      // clear. Reset to null to keep the save shape canonical.
      lastCheckpointId: null,
    };
    await SaveManager.save(existing);
  } catch (err) {
    Logger.error('SpaceGameScreen', 'Failed to save space level progress', err);
  }
}
